/**
 * Code Snippets Library
 *
 * Central repository for reusable code snippets used across connectors.
 * Each snippet includes name, description, group, and code template.
 *
 * Groups:
 * - native: Built-in Python and framework utilities
 * - tracer: Event and operation tracing/logging
 * - http: HTTP requests and API integrations
 */

export const codeSnippets = [
  {
    id: 'tracer_success',
    group: 'tracer',
    category: 'Tracer',
    name: 'Tracer Success',
    description: 'Log a successful operation step with context metadata',
    code: `self.tracer.success("{step_name}", {
    "event_type": event_data.get("type"),
    "company_id": self.company_id,
})`,
    tags: ['logging', 'tracer', 'success'],
    shortcut: 'ts',
  },
  {
    id: 'tracer_skip',
    group: 'tracer',
    category: 'Tracer',
    name: 'Tracer Skip',
    description: 'Log when a processing step is skipped with reason',
    code: `self.tracer.skip("{step_name}", {
    "reason": "{reason}",
    "event_type": event_data.get("type"),
    "company_id": self.company_id,
})`,
    tags: ['logging', 'tracer', 'skip'],
    shortcut: 'tsk',
  },
  {
    id: 'tracer_error',
    group: 'tracer',
    category: 'Tracer',
    name: 'Tracer Error',
    description: 'Log an error during processing with error details',
    code: `self.tracer.error("{step_name}", {
    "error": str(e),
    "error_type": type(e).__name__,
    "event_type": event_data.get("type"),
    "company_id": self.company_id,
})`,
    tags: ['logging', 'tracer', 'error'],
    shortcut: 'te',
  },
  {
    id: 'httpx_get',
    group: 'http',
    category: 'HTTP',
    name: 'HTTP GET Request',
    description: 'Perform a GET request using httpx with error handling',
    code: `import httpx

try:
    async with httpx.AsyncClient(timeout=30) as client:
        response = await client.get(
            "{api_url}",
            headers={"Authorization": f"Bearer {token}"},
            params={"param": "value"}
        )
        response.raise_for_status()
        data = response.json()
        self.tracer.success("get_request", {"status": response.status_code})
except httpx.HTTPError as e:
    self.tracer.error("get_request", {"error": str(e)})
    raise`,
    tags: ['httpx', 'api', 'get', 'request'],
    shortcut: 'hget',
  },
  {
    id: 'httpx_post',
    group: 'http',
    category: 'HTTP',
    name: 'HTTP POST Request',
    description: 'Perform a POST request using httpx with payload and error handling',
    code: `import httpx

try:
    async with httpx.AsyncClient(timeout=30) as client:
        response = await client.post(
            "{api_url}",
            headers={"Authorization": f"Bearer {token}", "Content-Type": "application/json"},
            json={"key": "value"}
        )
        response.raise_for_status()
        data = response.json()
        self.tracer.success("post_request", {"status": response.status_code})
except httpx.HTTPError as e:
    self.tracer.error("post_request", {"error": str(e)})
    raise`,
    tags: ['httpx', 'api', 'post', 'request'],
    shortcut: 'hpost',
  },
  {
    id: 'httpx_put',
    group: 'http',
    category: 'HTTP',
    name: 'HTTP PUT Request',
    description: 'Perform a PUT request using httpx to update a resource',
    code: `import httpx

try:
    async with httpx.AsyncClient(timeout=30) as client:
        response = await client.put(
            "{api_url}/{resource_id}",
            headers={"Authorization": f"Bearer {token}", "Content-Type": "application/json"},
            json={"key": "value"}
        )
        response.raise_for_status()
        data = response.json()
        self.tracer.success("put_request", {"status": response.status_code})
except httpx.HTTPError as e:
    self.tracer.error("put_request", {"error": str(e)})
    raise`,
    tags: ['httpx', 'api', 'put', 'request'],
    shortcut: 'hput',
  },
  {
    id: 'httpx_patch',
    group: 'http',
    category: 'HTTP',
    name: 'HTTP PATCH Request',
    description: 'Perform a PATCH request using httpx to partially update a resource',
    code: `import httpx

try:
    async with httpx.AsyncClient(timeout=30) as client:
        response = await client.patch(
            "{api_url}/{resource_id}",
            headers={"Authorization": f"Bearer {token}", "Content-Type": "application/json"},
            json={"key": "value"}
        )
        response.raise_for_status()
        data = response.json()
        self.tracer.success("patch_request", {"status": response.status_code})
except httpx.HTTPError as e:
    self.tracer.error("patch_request", {"error": str(e)})
    raise`,
    tags: ['httpx', 'api', 'patch', 'request'],
    shortcut: 'hpatch',
  },
  {
    id: 'httpx_delete',
    group: 'http',
    category: 'HTTP',
    name: 'HTTP DELETE Request',
    description: 'Perform a DELETE request using httpx to remove a resource',
    code: `import httpx

try:
    async with httpx.AsyncClient(timeout=30) as client:
        response = await client.delete(
            "{api_url}/{resource_id}",
            headers={"Authorization": f"Bearer {token}"}
        )
        response.raise_for_status()
        self.tracer.success("delete_request", {"status": response.status_code})
except httpx.HTTPError as e:
    self.tracer.error("delete_request", {"error": str(e)})
    raise`,
    tags: ['httpx', 'api', 'delete', 'request'],
    shortcut: 'hdel',
  },
  {
    id: 'config_company_load',
    group: 'native',
    category: 'Configuration',
    name: 'Load Company Global Configs',
    description: 'Load all global company configurations with template variable expansion',
    code: `from connectors.config_helper import get_all_company_config_values

# Load all company global configurations (template variables are expanded automatically)
company_cfg = get_all_company_config_values(str(self.company_id))

# Access configuration values - if they reference other configs via {{key}},
# those variables are automatically substituted
base_url = company_cfg.get("BASE_URL")
tenant_id = company_cfg.get("TENANT_ID")
webhook_url = company_cfg.get("WEBHOOK_URL")  # May contain expanded variable references

self.tracer.success("load_company_config", {
    "company_id": self.company_id,
    "config_count": len(company_cfg),
})`,
    tags: ['config', 'company', 'configuration', 'variables'],
    shortcut: 'cfg',
  },
  {
    id: 'config_integration_load',
    group: 'native',
    category: 'Configuration',
    name: 'Load Integration Parameters',
    description: 'Load all integration-specific parameters for company setup',
    code: `from connectors.config_helper import get_all_integration_config_values

# Load all parameters for this integration + company combination
cfg = get_all_integration_config_values(str(self.integration_id), str(self.company_id))

# Access integration-specific configuration values
api_url = cfg.get("api_url")
api_key = cfg.get("api_key")
api_username = cfg.get("api_username")
api_password = cfg.get("api_password")

self.tracer.success("load_integration_config", {
    "integration_id": self.integration_id,
    "company_id": self.company_id,
})`,
    tags: ['config', 'integration', 'parameters', 'credentials'],
    shortcut: 'icfg',
  },
  {
    id: 'config_integration_filter_configure',
    group: 'native',
    category: 'Configuration',
    name: 'Configure Integration Filter Fields',
    description: 'Configure filter field mappings for a company-integration pair. Syncs with existing configurations (fields already used to store event values are deactivated, not deleted, if removed).',
    code: `from connectors.config_helper import configure_integration_filter_fields

# Configure filter fields for this integration + company combination.
# Syncs with the current configuration: new fields are created, existing
# fields have their label updated, and fields removed from this dict are
# deleted - UNLESS the platform already recorded event values for them, in
# which case they're deactivated instead (kept for the values already
# stored) and reactivated automatically if they reappear in a later call.
filter_fields = {
    "data.idUnidade": "ID Unidade",
    "specialty_code": "Código de Especialidade",
    "patient_id": "ID do Paciente",
}

success = configure_integration_filter_fields(
    integration_id=str(self.integration_id),
    company_id=str(self.company_id),
    filter_fields=filter_fields
)

if success:
    self.tracer.success("configure_integration_filter_fields", {
        "integration_id": self.integration_id,
        "company_id": self.company_id,
        "fields_count": len(filter_fields),
    })
else:
    self.tracer.error("configure_integration_filter_fields", {
        "integration_id": self.integration_id,
        "company_id": self.company_id,
    })`,
    tags: ['config', 'filter', 'fields', 'integration', 'configuration'],
    shortcut: 'cfig',
  },
]

/**
 * Get all unique groups from snippets
 */
export const getAvailableGroups = () => {
  const groups = new Set(codeSnippets.map(snippet => snippet.group))
  return Array.from(groups).sort()
}

/**
 * Get group metadata
 */
export const getGroupMetadata = (group) => {
  const metadata = {
    tracer: {
      label: 'Tracer',
      description: 'Event and operation tracing/logging',
    },
    http: {
      label: 'HTTP',
      description: 'HTTP requests and API integrations',
    },
    native: {
      label: 'Native',
      description: 'Built-in Python and framework utilities',
    },
  }
  return metadata[group] || { label: group, description: '' }
}

/**
 * Filter snippets by search term
 */

export const getSnippetsByCategory = () => {
  const grouped = {}
  codeSnippets.forEach(snippet => {
    if (!grouped[snippet.category]) {
      grouped[snippet.category] = []
    }
    grouped[snippet.category].push(snippet)
  })
  return grouped
}

export const searchSnippets = (query) => {
  const q = query.toLowerCase()
  return codeSnippets.filter(
    snippet =>
      snippet.name.toLowerCase().includes(q) ||
      snippet.description.toLowerCase().includes(q) ||
      snippet.tags.some(tag => tag.toLowerCase().includes(q))
  )
}
